import {
  EmodeDataHumanized,
  ReservesDataHumanized,
  UiPoolDataProvider,
  UserReserveDataHumanized,
} from '@aave/contract-helpers';
import { Provider } from '@ethersproject/providers';
import { Contract } from 'ethers';
import { CustomMarket, MarketDataType } from 'src/ui-config/marketsConfig';

export type UserReservesDataHumanized = {
  userReserves: UserReserveDataHumanized[];
  userEmodeCategoryId: number;
};

const dinolendUiPoolDataProviderAbi = [
  'function getReservesData(address provider) view returns ((address underlyingAsset,string name,string symbol,uint256 decimals,uint256 baseLTVasCollateral,uint256 reserveLiquidationThreshold,uint256 reserveLiquidationBonus,uint256 reserveFactor,bool usageAsCollateralEnabled,bool borrowingEnabled,bool stableBorrowRateEnabled,bool isActive,bool isFrozen,uint128 liquidityIndex,uint128 variableBorrowIndex,uint128 liquidityRate,uint128 variableBorrowRate,uint128 stableBorrowRate,uint40 lastUpdateTimestamp,address aTokenAddress,address stableDebtTokenAddress,address variableDebtTokenAddress,address interestRateStrategyAddress,uint256 availableLiquidity,uint256 totalPrincipalStableDebt,uint256 averageStableRate,uint256 stableDebtLastUpdateTimestamp,uint256 totalScaledVariableDebt,uint256 priceInMarketReferenceCurrency,address priceOracle,uint256 variableRateSlope1,uint256 variableRateSlope2,uint256 stableRateSlope1,uint256 stableRateSlope2,uint256 baseStableBorrowRate,uint256 baseVariableBorrowRate,uint256 optimalUsageRatio,bool isPaused,bool isSiloedBorrowing,uint128 accruedToTreasury,uint128 unbacked,uint128 isolationModeTotalDebt,bool flashLoanEnabled,uint256 debtCeiling,uint256 debtCeilingDecimals,uint8 eModeCategoryId,uint256 borrowCap,uint256 supplyCap,uint16 eModeLtv,uint16 eModeLiquidationThreshold,uint16 eModeLiquidationBonus,address eModePriceSource,string eModeLabel,bool borrowableInIsolation)[],(uint256 marketReferenceCurrencyUnit,int256 marketReferenceCurrencyPriceInUsd,int256 networkBaseTokenPriceInUsd,uint8 networkBaseTokenPriceDecimals))',
  'function getUserReservesData(address provider,address user) view returns ((address underlyingAsset,uint256 scaledATokenBalance,bool usageAsCollateralEnabledOnUser,uint256 stableBorrowRate,uint256 scaledVariableDebt,uint256 principalStableDebt,uint256 stableBorrowLastUpdateTimestamp)[],uint8)',
];

const isDinolendMarket = (marketData: MarketDataType) =>
  marketData.market === CustomMarket.dinolend_configured_v3;

const toNumber = (value: { toString(): string }) => Number(value.toString());

type ContractNumber = {
  toString(): string;
};

type DinolendReserveRaw = {
  underlyingAsset: string;
  name: string;
  symbol: string;
  decimals: ContractNumber;
  baseLTVasCollateral: ContractNumber;
  reserveLiquidationThreshold: ContractNumber;
  reserveLiquidationBonus: ContractNumber;
  reserveFactor: ContractNumber;
  usageAsCollateralEnabled: boolean;
  borrowingEnabled: boolean;
  isActive: boolean;
  isFrozen: boolean;
  liquidityIndex: ContractNumber;
  variableBorrowIndex: ContractNumber;
  liquidityRate: ContractNumber;
  variableBorrowRate: ContractNumber;
  lastUpdateTimestamp: ContractNumber;
  aTokenAddress: string;
  variableDebtTokenAddress: string;
  interestRateStrategyAddress: string;
  availableLiquidity: ContractNumber;
  totalScaledVariableDebt: ContractNumber;
  priceInMarketReferenceCurrency: ContractNumber;
  priceOracle: string;
  variableRateSlope1: ContractNumber;
  variableRateSlope2: ContractNumber;
  baseVariableBorrowRate: ContractNumber;
  optimalUsageRatio: ContractNumber;
  isPaused: boolean;
  isSiloedBorrowing: boolean;
  accruedToTreasury: ContractNumber;
  isolationModeTotalDebt: ContractNumber;
  flashLoanEnabled: boolean;
  debtCeiling: ContractNumber;
  debtCeilingDecimals: ContractNumber;
  borrowCap: ContractNumber;
  supplyCap: ContractNumber;
  borrowableInIsolation: boolean;
};

type DinolendPoolBaseCurrencyRaw = {
  marketReferenceCurrencyUnit: ContractNumber;
  marketReferenceCurrencyPriceInUsd: ContractNumber;
  networkBaseTokenPriceInUsd: ContractNumber;
  networkBaseTokenPriceDecimals: number;
};

type DinolendUserReserveRaw = {
  underlyingAsset: string;
  scaledATokenBalance: ContractNumber;
  usageAsCollateralEnabledOnUser: boolean;
  scaledVariableDebt: ContractNumber;
};

export class UiPoolService {
  constructor(private readonly getProvider: (chainId: number) => Provider) {}

  private async getUiPoolDataService(marketData: MarketDataType) {
    const provider = this.getProvider(marketData.chainId);
    return new UiPoolDataProvider({
      uiPoolDataProviderAddress: marketData.addresses.UI_POOL_DATA_PROVIDER,
      provider,
      chainId: marketData.chainId,
    });
  }

  async getReservesHumanized(marketData: MarketDataType): Promise<ReservesDataHumanized> {
    if (isDinolendMarket(marketData)) {
      return this.getDinolendReservesHumanized(marketData);
    }

    const uiPoolDataProvider = await this.getUiPoolDataService(marketData);
    return uiPoolDataProvider.getReservesHumanized({
      lendingPoolAddressProvider: marketData.addresses.LENDING_POOL_ADDRESS_PROVIDER,
    });
  }

  async getUserReservesHumanized(
    marketData: MarketDataType,
    user: string
  ): Promise<UserReservesDataHumanized> {
    if (isDinolendMarket(marketData)) {
      return this.getDinolendUserReservesHumanized(marketData, user);
    }

    const uiPoolDataProvider = await this.getUiPoolDataService(marketData);
    return uiPoolDataProvider.getUserReservesHumanized({
      user,
      lendingPoolAddressProvider: marketData.addresses.LENDING_POOL_ADDRESS_PROVIDER,
    });
  }

  async getEModesHumanized(marketData: MarketDataType): Promise<EmodeDataHumanized[]> {
    if (isDinolendMarket(marketData)) {
      return [];
    }

    const uiPoolDataProvider = await this.getUiPoolDataService(marketData);
    return uiPoolDataProvider.getEModesHumanized({
      lendingPoolAddressProvider: marketData.addresses.LENDING_POOL_ADDRESS_PROVIDER,
    });
  }

  private getDinolendUiPoolDataProvider(marketData: MarketDataType) {
    const provider = this.getProvider(marketData.chainId);
    return new Contract(
      marketData.addresses.UI_POOL_DATA_PROVIDER,
      dinolendUiPoolDataProviderAbi,
      provider
    );
  }

  private async getDinolendReservesHumanized(
    marketData: MarketDataType
  ): Promise<ReservesDataHumanized> {
    const dataProvider = this.getDinolendUiPoolDataProvider(marketData);
    const [reservesRaw, poolBaseCurrencyRaw] = (await dataProvider.getReservesData(
      marketData.addresses.LENDING_POOL_ADDRESS_PROVIDER
    )) as [DinolendReserveRaw[], DinolendPoolBaseCurrencyRaw];

    const reservesData = reservesRaw.map((reserveRaw, index) => ({
      originalId: index,
      id: `${marketData.chainId}-${reserveRaw.underlyingAsset}-${marketData.addresses.LENDING_POOL_ADDRESS_PROVIDER}`.toLowerCase(),
      underlyingAsset: reserveRaw.underlyingAsset.toLowerCase(),
      name: reserveRaw.name,
      symbol: reserveRaw.symbol,
      decimals: toNumber(reserveRaw.decimals),
      baseLTVasCollateral: reserveRaw.baseLTVasCollateral.toString(),
      reserveLiquidationThreshold: reserveRaw.reserveLiquidationThreshold.toString(),
      reserveLiquidationBonus: reserveRaw.reserveLiquidationBonus.toString(),
      reserveFactor: reserveRaw.reserveFactor.toString(),
      usageAsCollateralEnabled: reserveRaw.usageAsCollateralEnabled,
      borrowingEnabled: reserveRaw.borrowingEnabled,
      isActive: reserveRaw.isActive,
      isFrozen: reserveRaw.isFrozen,
      liquidityIndex: reserveRaw.liquidityIndex.toString(),
      variableBorrowIndex: reserveRaw.variableBorrowIndex.toString(),
      liquidityRate: reserveRaw.liquidityRate.toString(),
      variableBorrowRate: reserveRaw.variableBorrowRate.toString(),
      lastUpdateTimestamp: toNumber(reserveRaw.lastUpdateTimestamp),
      aTokenAddress: reserveRaw.aTokenAddress.toString(),
      variableDebtTokenAddress: reserveRaw.variableDebtTokenAddress.toString(),
      interestRateStrategyAddress: reserveRaw.interestRateStrategyAddress.toString(),
      availableLiquidity: reserveRaw.availableLiquidity.toString(),
      totalScaledVariableDebt: reserveRaw.totalScaledVariableDebt.toString(),
      priceInMarketReferenceCurrency: reserveRaw.priceInMarketReferenceCurrency.toString(),
      priceOracle: reserveRaw.priceOracle,
      variableRateSlope1: reserveRaw.variableRateSlope1.toString(),
      variableRateSlope2: reserveRaw.variableRateSlope2.toString(),
      baseVariableBorrowRate: reserveRaw.baseVariableBorrowRate.toString(),
      optimalUsageRatio: reserveRaw.optimalUsageRatio.toString(),
      isPaused: reserveRaw.isPaused,
      isSiloedBorrowing: reserveRaw.isSiloedBorrowing,
      accruedToTreasury: reserveRaw.accruedToTreasury.toString(),
      isolationModeTotalDebt: reserveRaw.isolationModeTotalDebt.toString(),
      flashLoanEnabled: reserveRaw.flashLoanEnabled,
      debtCeiling: reserveRaw.debtCeiling.toString(),
      debtCeilingDecimals: toNumber(reserveRaw.debtCeilingDecimals),
      borrowCap: reserveRaw.borrowCap.toString(),
      supplyCap: reserveRaw.supplyCap.toString(),
      borrowableInIsolation: reserveRaw.borrowableInIsolation,
      virtualUnderlyingBalance: reserveRaw.availableLiquidity.toString(),
      deficit: '0',
    }));

    return {
      reservesData,
      baseCurrencyData: {
        marketReferenceCurrencyDecimals:
          poolBaseCurrencyRaw.marketReferenceCurrencyUnit.toString().length - 1,
        marketReferenceCurrencyPriceInUsd:
          poolBaseCurrencyRaw.marketReferenceCurrencyPriceInUsd.toString(),
        networkBaseTokenPriceInUsd: poolBaseCurrencyRaw.networkBaseTokenPriceInUsd.toString(),
        networkBaseTokenPriceDecimals: poolBaseCurrencyRaw.networkBaseTokenPriceDecimals,
      },
    };
  }

  private async getDinolendUserReservesHumanized(
    marketData: MarketDataType,
    user: string
  ): Promise<UserReservesDataHumanized> {
    const dataProvider = this.getDinolendUiPoolDataProvider(marketData);
    const [userReservesRaw, userEmodeCategoryId] = (await dataProvider.getUserReservesData(
      marketData.addresses.LENDING_POOL_ADDRESS_PROVIDER,
      user
    )) as [DinolendUserReserveRaw[], number];

    return {
      userEmodeCategoryId,
      userReserves: userReservesRaw.map((userReserveRaw) => ({
        id: `${marketData.chainId}-${user}-${userReserveRaw.underlyingAsset}-${marketData.addresses.LENDING_POOL_ADDRESS_PROVIDER}`.toLowerCase(),
        underlyingAsset: userReserveRaw.underlyingAsset.toLowerCase(),
        scaledATokenBalance: userReserveRaw.scaledATokenBalance.toString(),
        usageAsCollateralEnabledOnUser: userReserveRaw.usageAsCollateralEnabledOnUser,
        scaledVariableDebt: userReserveRaw.scaledVariableDebt.toString(),
      })),
    };
  }
}
